import React, { useState } from 'react';
import { Select, Button, Input, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { Add16Regular, Edit16Regular } from '@fluentui/react-icons';
import { Account } from '../types';

interface AccountSwitcherProps {
  accounts: Account[];
  selectedAccountId: number | null;
  onSelect: (id: number) => void;
  onCreate: (name: string) => void;
  onRename: (id: number, name: string) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  select: {
    minWidth: '180px',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
  newAccountForm: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

export const AccountSwitcher: React.FC<AccountSwitcherProps> = ({
  accounts, selectedAccountId, onSelect, onCreate, onRename,
}) => {
  const styles = useStyles();
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [showRename, setShowRename] = useState(false);
  const [renameName, setRenameName] = useState('');

  const handleCreate = () => {
    if (newName.trim()) {
      onCreate(newName.trim());
      setNewName('');
      setShowNew(false);
    }
  };

  const handleRename = () => {
    if (renameName.trim() && selectedAccountId) {
      onRename(selectedAccountId, renameName.trim());
      setShowRename(false);
    }
  };

  const startRename = () => {
    const current = accounts.find(a => a.id === selectedAccountId);
    setRenameName(current?.name || '');
    setShowRename(true);
  };

  if (showRename) {
    return (
      <div className={styles.newAccountForm}>
        <Input
          size="small"
          value={renameName}
          onChange={(e, d) => setRenameName(d.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setShowRename(false); }}
          placeholder="Account name"
          autoFocus
          style={{ width: '160px' }}
        />
        <Button size="small" appearance="primary" onClick={handleRename}>Save</Button>
        <Button size="small" appearance="transparent" onClick={() => setShowRename(false)} style={{ color: 'inherit' }}>✕</Button>
      </div>
    );
  }

  if (showNew) {
    return (
      <div className={styles.newAccountForm}>
        <Input
          size="small"
          value={newName}
          onChange={(e, d) => setNewName(d.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowNew(false); }}
          placeholder="Account name"
          autoFocus
          style={{ width: '160px' }}
        />
        <Button size="small" appearance="primary" onClick={handleCreate}>Add</Button>
        <Button size="small" appearance="transparent" onClick={() => setShowNew(false)} style={{ color: 'inherit' }}>✕</Button>
      </div>
    );
  }

  return (
    <div className={`${styles.container} account-switcher`}>
      <Select
        size="small"
        value={String(selectedAccountId || '')}
        onChange={(e, d) => onSelect(Number(d.value))}
        className={styles.select}
        style={{ color: '#242424', backgroundColor: '#fff' }}
      >
        {accounts.map(a => (
          <option key={a.id} value={String(a.id)}>
            {a.name}{a.owner_name && a.access_type !== 'owner' ? ` (${a.owner_name})` : ''}
          </option>
        ))}
      </Select>
      <Tooltip content="Rename this account" relationship="label">
        <Button size="small" appearance="transparent" icon={<Edit16Regular style={{ color: 'inherit' }} />}
          onClick={startRename} style={{ color: 'inherit' }} />
      </Tooltip>
      <Tooltip content="Create a new account" relationship="label">
        <Button size="small" appearance="transparent" icon={<Add16Regular style={{ color: 'inherit' }} />}
          onClick={() => setShowNew(true)} style={{ color: 'inherit' }} />
      </Tooltip>
    </div>
  );
};
